import { useEffect, useState } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { protectedResources } from "../../authConfig";
import { useFetchWithMsal } from "../../hooks/useFetchWithMsal";
import { LoadPage } from "../../components/LoadPage";
import { CommentType, CommentTypeNames, ObjectTypeNames, ObjectTypes } from "../../models/comments";
import { AddComment } from "./AddCommentBox";
import { Link } from "react-router-dom";

interface CommentListProps {
    endpoint: string,
    execute: Function,
    isLoading: boolean,
    commentTypeLink?: boolean,
    children?: React.ReactNode
}

export const CommentList = (props: CommentListProps) => {
    const [comments, setComments] = useState(undefined as Array<any> | undefined);
    const getData = () => {
        props.execute("GET", props.endpoint).then((result: any) => {
            if (result?.status === 404) {
                setComments([]);
            } else {
                setComments(result);
            }
        });
    }

    const commentAuthor = (comment: any) => {
        if (comment.commentTypeId === CommentType.AutomaticallyGenerated) {
            return "System";
        } else {
            return comment.appUser?.email ? comment.appUser?.email : "Unknown";
        }
    }

    const getObjecTypNames = (comment: any) => {
        if (props.commentTypeLink && ObjectTypeNames[comment.objectTypeId].linkTo) {
            return <Link to={ObjectTypeNames[comment.objectTypeId].linkTo + comment.objectId}>
                {ObjectTypeNames[comment.objectTypeId].name}
            </Link>;
        } else {
            return ObjectTypeNames[comment.objectTypeId].name;
        }
    }

    useEffect(() => {
        if (!comments) {
            getData();
        }
    }, [props.execute]);

    return <LoadPage isLoading={props.isLoading}>
        {comments ?
            comments?.map((comment: any) =>
                <div className="comment-bubble p-4 rounded-lg streck-secondary" key={"CommentList" + comment.id}>
                    <span className="comment-bubble-text">
                        {comment.text}
                    </span>
                    <span className="comment-bubble-text">
                        <span style={{ width: "225px", display: "inline-block" }}>
                            {CommentTypeNames[comment.commentTypeId].name}
                        </span>
                        <span style={{ textAlign: "right", width: "225px", display: "inline-block" }}>
                            {getObjecTypNames(comment)}
                        </span>
                    </span>
                    <span className="comment-bubble-text">
                        <span style={{ width: "225px", display: "inline-block" }}>
                            {commentAuthor(comment)}
                        </span>
                        <span style={{ textAlign: "right", width: "225px", display: "inline-block" }}>
                            {new Date(comment.createdOn).toLocaleDateString("en-us", { year: "numeric", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Chicago" })}
                        </span>
                    </span>
                </div>) :
            <div>
                There are no comments on this kit
            </div>}
    </LoadPage>;
}

interface CommentListAndButtonProps {
    objectId: number,
    objectType: ObjectTypes,
    commentTypeLink?: boolean,
    isReadOnly?: boolean,
    children?: React.ReactNode
}

export const CommentListAndButton = (props: CommentListAndButtonProps) => {
    const [commentListKey, setCommentListKey] = useState(Date.now());
    const [showAddComment, setShowAddComment] = useState(false);
    const objectTypeNames = ObjectTypeNames[props.objectType].name;
    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });
    return <>
        {showAddComment &&
            <Window title={"Add comment"} onClose={() => { setShowAddComment(false); }} initialHeight={375} initialWidth={400}>
                <AddComment
                    loginPrompt={false}
                    objectId={props.objectId} objectTypeId={props.objectType}
                    buttonText={"Add " + objectTypeNames + " Comment"} header=""
                    endpoint={protectedResources.apiComment.endpoint + "CreateComment"}
                    afterAddFunction={() => { setCommentListKey(Date.now()); setShowAddComment(false); }} execute={execute}></AddComment>
            </Window>}
        <CommentList
            key={commentListKey}
            isLoading={isLoading}
            endpoint={protectedResources.apiComment.endpoint + "GetAllCommentsForObject?objectTypeId=" + props.objectType + "&objectId=" + props.objectId}
            execute={execute}>
        </CommentList>
        <Button
            onClick={() => setShowAddComment(true)}
            type="submit"
            hidden={props.isReadOnly}
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
            Add {objectTypeNames} Comment</Button>
    </>;
}
